import type { ControlAction } from "../../atoms/control-action.constants";
import { isSafeHref } from "../../services/safe-href";
import type { RecommenderCardLogo, RecommenderCardProps, RecommenderCardVariant, RecommenderMissingItem } from "./recommender-card";
import type { AlternativeKind, CardMode, RecommenderCard, RecommenderCardProduct, RecommenderPrice } from "./dynamic-recommender-api";
import { MAX_PINNED_CHANNELS, type ProductSettings } from "./dynamic-recommender-products";

/** The copy a card draws that the engine does not supply. */
export interface CardCopy {
  streamingTypeLabel: string;
  genrePackTypeLabel: string;
  comboTypeLabel: string;
  miniTypeLabel: string;
  includesLabel: string;
  notIncludedLabel: string;
  /** The line over a bundle's call to action; `{n}` is the bundle's monthly total. */
  subtotalLabel: string;
  /** The line under an add-on's channels; `{addon}` is replaced by its name. */
  addonHintLabel: string;
  /** The line under an interest missed for its core channels; `{channels}` is replaced by them. */
  coreMissingLabel: string;
  /** Core channel names that line shows before it counts the rest. */
  coreMissingCount: number;
  legalContent: string;
  legalLinkLabel: string;
  legalLinkUrl: string;
  /** What the legal link does when clicked. */
  legalLinkAction: ControlAction;
  rsnFeeLabel: string;
  promoLabel: string;
  /** The channel link without a count. */
  viewAllLabel: string;
  /** The channel link with one; `{n}` is the count. */
  viewAllCountLabel: string;
  viewAllUrl: string;
  /** The call to action's destination when a product item sets none. */
  ctaUrl: string;
}

/** One results slot: a card, or a base card and its mini pack, drawn in one rail. */
export interface CardSlot {
  base: RecommenderCardProps;
  minis: RecommenderCardProps[];
  /** The slot's one call to action, from the base card's item with its mini packs added. Empty, no button. */
  ctaHref: string;
  /** Why the engine chose it, flagged on the rail's corner. */
  tag: string;
  /** The picks the slot's package leaves out, listed under the cards. */
  notIncluded: RecommenderMissingItem[];
  /** A bundle's total over its button; empty for a single card. */
  subtotal: string;
  /** What the slot costs a month, its mini packs included, with no promo line; an asterisk marks a promotional price.
      Its small print is the base card's, fees and required add-ons included. */
  total: { price_dollars: string; price_cents: string; legal_content: string };
}

/** Channel names one add-on bar lists before it counts the rest. */
const ADDON_NAMES_SHOWN = 3;

function channelNames(names: string[], shown = ADDON_NAMES_SHOWN): string {
  const rest = names.length - shown;
  return rest > 0 ? `${names.slice(0, shown).join(", ")} +${rest} more` : names.join(", ");
}

/**
 * One bar per interest, under it the core channels it lacks when that alone left it out;
 * one per channel no add-on brings; then one per add-on listing its channels.
 */
export function notIncludedItems(card: RecommenderCard, copy: CardCopy): RecommenderMissingItem[] {
  const groups = (card.missing.addons || []).filter(group => group.channels.length > 0);
  const grouped = new Set(groups.flatMap(group => group.channels));
  const coreByInterest = new Map((card.missing.core || []).filter(entry => entry.channels.length > 0).map(entry => [entry.interest, entry.channels]));
  return [
    ...card.missing.interests.map(label => {
      const core = coreByInterest.get(label);
      return core ? {
        label,
        hint: copy.coreMissingLabel.replace(/\{channels\}/g, () => channelNames(core, copy.coreMissingCount))
      } : { label };
    }),
    ...card.missing.channels.filter(name => !grouped.has(name)).map(label => ({ label })),
    ...groups.map(group => ({
      label: channelNames(group.channels),
      hint: copy.addonHintLabel.replace("{addon}", group.name)
    }))
  ];
}

/** Each card's tag says why the engine chose it. */
export function cardTags(mode: CardMode, kind: AlternativeKind): { top: string; alternative: string } {
  if (mode === "no-full-match") {
    return {
      top: "Closest Match",
      alternative: "2nd Closest Match"
    };
  }
  const alternative = kind === "cheaper" ? "Lower Price" :
    kind === "upgrade" ? "More of Your Picks" :
      kind === "fallback" ? "Get More" : "Another Option";
  return {
    top: "Best Match",
    alternative
  };
}

/** DVR and stream counts ride in the same list as the apps but have no logo. */
const NOT_AN_APP = /\b(dvr|streams?|screens?)\b/i;

const money = (n: number) => `$${n.toFixed(2)}`;

function displayName(code: string): string {
  return code
    .split(/\s*\+\s*/)
    .map(part => part.replace(/^DTVN_/, ""))
    .join(" + ");
}

function variantOf(card: RecommenderCard): RecommenderCardVariant {
  return /signature/i.test(card.productClass) ? "streaming" : "genre-pack";
}

function typeLabelOf(card: RecommenderCard, copy: CardCopy): string {
  if (/signature/i.test(card.productClass)) {
    return copy.streamingTypeLabel;
  }
  if (card.combo || /combo/i.test(card.productClass)) {
    return copy.comboTypeLabel;
  }
  return /mini/i.test(card.productClass) ? copy.miniTypeLabel : copy.genrePackTypeLabel;
}

/** Puts a value into a CMS line's "{n}". */
function fill(template: string, value: string | number): string {
  return template.replace(/\{n\}/g, String(value));
}

/** Dollars and the cents run the price block draws, e.g. "$49" and ".99/mo.". */
function priceParts(price: number, promoMonths: number | null, promoLabel: string) {
  const [dollars, cents] = price.toFixed(2).split(".");
  const promo = promoMonths && promoLabel ? ` ${fill(promoLabel, promoMonths)}` : "";
  return {
    price_dollars: `$${dollars}`,
    price_cents: `.${cents}/mo.${promo}`
  };
}

/* A required add-on that is not drawn as a rail card is still part of what the
   pick costs, so it rides the fine print beside the RSN fee rather than vanishing. */
function legalFor(price: RecommenderPrice | null, addons: RecommenderCard["requiredAddons"], copy: CardCopy): string {
  const fee = price?.rsnFee ? `${fill(copy.rsnFeeLabel, money(price.rsnFee))} ` : "";
  const required = addons.map(addon => `+ ${money(addon.price)}/mo. ${addon.name} `).join("");
  return `${fee}${required}${copy.legalContent}`.trim();
}

function appLogos(card: RecommenderCard): RecommenderCardLogo[] {
  return card.includedContent
    .filter(item => !NOT_AN_APP.test(item.name) && typeof item.logo === "string" && item.logo !== "")
    .map(item => ({
      logo_url: item.logo as string,
      logo_alt: item.name
    }));
}

/** The authored item the engine matched, if it is still in the list. */
function itemFor(products: ProductSettings[], product?: RecommenderCardProduct): ProductSettings | undefined {
  return product && Number.isInteger(product.index) ? products[product.index] : undefined;
}

/* The channel row draws only what the author pinned, as the engine resolved it in this market. */
function pinnedLogos(product?: RecommenderCardProduct): RecommenderCardLogo[] {
  return (product?.channels || [])
    .filter(channel => typeof channel.logo === "string" && channel.logo !== "")
    .map(channel => ({
      logo_url: channel.logo as string,
      logo_alt: channel.name
    }));
}

/** What a product item sets on its card: logos, the channel link, the glow and the legal link.
 *  `fallbackUrl` is the blok's link, which describes the base package, not a mini. */
function fromProduct(products: ProductSettings[], product: RecommenderCardProduct | undefined, copy: CardCopy, fallbackUrl: string): Partial<RecommenderCardProps> {
  const item = itemFor(products, product);
  const url = item?.viewAllUrl || fallbackUrl;
  return {
    channel_logos: item ? pinnedLogos(product) : [],
    more_label: url ? (item?.channelCount ? fill(copy.viewAllCountLabel, item.channelCount) : copy.viewAllLabel) : undefined,
    more_href: url || undefined,
    glow_color: item?.glowColor || undefined,
    legal_link_href: [item?.legalUrl, copy.legalLinkUrl].find(href => isSafeHref(href)) || ""
  };
}

/**
 * One line-up for every linked part when each link is the same modal with its own
 * `pkg` (`?pkg=myentertainment` + `?pkg=mykids` -> `?pkg=myentertainment,mykids`),
 * which lists packs given that way. Otherwise the first link.
 */
function comboLineupUrl(urls: string[]): string | undefined {
  const parsed = urls.map(url => {
    const [path, query = ""] = url.split("?");
    const params = new URLSearchParams(query);
    const pkg = params.get("pkg") || "";
    params.delete("pkg");
    return {
      path,
      pkg,
      rest: params.toString()
    };
  });
  const [first] = parsed;
  const joinable = parsed.length > 1 && !urls.some(url => url.includes("#")) &&
    parsed.every(p => p.pkg && !p.pkg.includes(",") && p.path === first.path && p.rest === first.rest);
  if (!joinable) {
    return urls[0];
  }
  const pkgs = [...new Set(parsed.map(p => encodeURIComponent(p.pkg)))].join(",");
  return `${first.path}?${first.rest ? `${first.rest}&` : ""}pkg=${pkgs}`;
}

const PRE_CONFIG_ITEMS = /([?&]preConfigItems=)([^&]*)/;

function preConfigItems(url: string): string[] {
  const value = PRE_CONFIG_ITEMS.exec(url.split("#")[0])?.[2] || "";
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    /* A malformed escape is kept as a literal. */
  }
  return decoded.split(",").filter(Boolean);
}

/**
 * A bundle's one button puts its mini packs in the cart with the base: each mini item's
 * `preConfigItems` joins the base link's (`mysports` + `myhome` -> `mysports,myhome`).
 * The base link's own value stays as authored, and a link without the parameter is left as it is.
 */
function bundleCtaUrl(baseUrl: string, miniUrls: string[]): string {
  const [query, ...fragment] = baseUrl.split("#");
  const authored = PRE_CONFIG_ITEMS.exec(query)?.[2];
  if (authored === undefined) {
    return baseUrl;
  }
  const seen = new Set(preConfigItems(query).map(item => item.toLowerCase()));
  const added = miniUrls.flatMap(preConfigItems).filter(item => {
    const key = item.toLowerCase();
    return !seen.has(key) && Boolean(seen.add(key));
  });
  if (added.length === 0) {
    return baseUrl;
  }
  const joined = [authored, ...added.map(encodeURIComponent)].filter(Boolean).join(",");
  return [query.replace(PRE_CONFIG_ITEMS, (_, key: string) => `${key}${joined}`), ...fragment].join("#");
}

/** A combo no item names draws its parts' pinned channels, a part at a time, and links
 *  to its linked parts' line-ups: no single item holds both packs. */
function fromComboParts(products: ProductSettings[], parts: RecommenderCardProduct[], copy: CardCopy): Partial<RecommenderCardProps> {
  const perPart = parts.map(part => (itemFor(products, part) ? pinnedLogos(part) : []));
  const logos: RecommenderCardLogo[] = [];
  for (let i = 0; logos.length < MAX_PINNED_CHANNELS && perPart.some(list => i < list.length); i += 1) {
    for (const logo of perPart.map(list => list[i])) {
      if (logo && logos.length < MAX_PINNED_CHANNELS && !logos.some(l => l.logo_url === logo.logo_url)) {
        logos.push(logo);
      }
    }
  }
  const url = comboLineupUrl(parts.map(part => itemFor(products, part)?.viewAllUrl || "").filter(Boolean));
  return {
    channel_logos: logos,
    ...(url ? {
      more_label: copy.viewAllLabel,
      more_href: url
    } : null)
  };
}

function shared(copy: CardCopy): Partial<RecommenderCardProps> {
  return {
    includes_label: copy.includesLabel,
    legal_link_label: copy.legalLinkLabel,
    legal_link_action: copy.legalLinkAction
  };
}

/**
 * The RecommenderCard props for one engine card. A card whose package needs a mini pack
 * (MyHome Team for a local team) adds one card per mini to its rail. The tag, the
 * missing picks and a bundle's one call to action belong to the slot, not a card.
 */
export function toCardSlot(
  card: RecommenderCard,
  tagLabel: string,
  copy: CardCopy,
  idPrefix: string,
  products: ProductSettings[] = []
): CardSlot | null {
  const price = card?.price;
  if (!card || typeof card.code !== "string" || !price) {
    return null;
  }
  const legal = legalFor(price, (card.requiredAddons || []).filter(addon => !addon.mini), copy);
  const base: RecommenderCardProps = {
    ...shared(copy),
    id: idPrefix,
    card_variant: variantOf(card),
    name_label: card.name?.trim() || displayName(card.code),
    type_label: typeLabelOf(card, copy),
    heading_level: "h4",
    app_logos: appLogos(card),
    ...fromProduct(products, card.product, copy, copy.viewAllUrl),
    ...(!card.product && card.combo?.products?.length ? fromComboParts(products, card.combo.products, copy) : null),
    strikethrough_price: price.regularPrice && price.regularPrice > price.price ? `${money(price.regularPrice)}/mo.` : undefined,
    ...priceParts(price.price, price.promoDuration, copy.promoLabel),
    legal_content: legal
  };
  const miniAddons = (card.requiredAddons || []).filter(addon => addon.mini);
  const total = miniAddons.reduce((sum, addon) => sum + addon.price, price.price);
  const promotional = Boolean(price.promoDuration) || miniAddons.some(addon => addon.promoDuration);
  const totalParts = priceParts(total, null, "");
  const minis = miniAddons
    .map((addon, index): RecommenderCardProps => ({
      ...shared(copy),
      id: `${idPrefix}-mini-${index}`,
      card_variant: "genre-pack",
      glow_variant: "blue",
      name_label: addon.name,
      type_label: copy.miniTypeLabel,
      heading_level: "h4",
      ...fromProduct(products, addon.product, copy, ""),
      strikethrough_price: addon.regularPrice && addon.regularPrice > addon.price ? `${money(addon.regularPrice)}/mo.` : undefined,
      ...priceParts(addon.price, addon.promoDuration ?? null, copy.promoLabel),
      legal_content: copy.legalContent
    }));
  return {
    base,
    minis,
    ctaHref: bundleCtaUrl(
      [itemFor(products, card.product)?.ctaUrl, copy.ctaUrl].find(url => isSafeHref(url)) || "",
      miniAddons.map(addon => itemFor(products, addon.product)?.ctaUrl || "")
    ),
    tag: tagLabel,
    notIncluded: notIncludedItems(card, copy),
    subtotal: minis.length > 0 ? fill(copy.subtotalLabel, money(total)) : "",
    total: {
      ...totalParts,
      price_cents: promotional ? `${totalParts.price_cents}*` : totalParts.price_cents,
      legal_content: legal
    }
  };
}
